import Avatar from '@ui/Avatar';
import { ReviewDocument } from 'models/review';
import { PublicUserDocument } from 'models/user';
import Link from 'next/link';
import { StatusLabel, useOnlineStatus } from '@util/hooks/useOnlineStatus';
import { useChat } from 'context/ChatContext';
import {
  AccurateDescriberIcon,
  FastShipperIcon,
  QuickResponderIcon,
} from './icons';
import ReviewCard from './cards/ReviewCard';
import CarouselComponent from '@ui/Carousel';
import { useAuth } from 'context/AuthContext';

export default function ProductMeetTheSeller({
  sellerDoc,
  reviews,
  product_id,
}: {
  sellerDoc: PublicUserDocument;
  reviews: ReviewDocument[];
  product_id: string;
}) {
  const { userDoc } = useAuth();
  const { openChatModalWithRecipientAndProductId } = useChat();

  const { status } = useOnlineStatus(sellerDoc?.uid ?? '');

  return (
    <div className="flex w-full flex-col gap-y-16 bg-[#F6F6F6]">
      <h1 className="px-16 text-[3rem] font-semibold text-black">
        Meet The Seller
      </h1>

      <div className="flex flex-col items-start gap-x-8  py-4 sm:flex-row sm:px-16">
        <div className="flex w-full flex-shrink-0 flex-col gap-y-4 rounded-[2rem] bg-white p-[2rem] shadow-sm sm:w-[35rem]">
          <div className="flex items-center gap-x-6">
            <Avatar user={sellerDoc} size="medium" />

            <div className="flex flex-col gap-y-2">
              <Link
                href={`/profile/${sellerDoc?.username_slug}`}
                className="text-[1.6rem] font-bold text-brand-secondary hover:underline"
              >
                {sellerDoc?.username}
              </Link>

              <StatusLabel
                status={status}
                hide_status={sellerDoc.hide_status}
                showLastLogin={userDoc?.roles?.support}
                recipientDocUid={sellerDoc.uid}
              />
            </div>
          </div>

          <div className="mt-2 flex w-full items-center justify-between px-[2rem]">
            <div className="flex flex-col items-center gap-y-2">
              <div className="flex items-start gap-x-2">
                <span className="text-[2.1rem] font-semibold text-black">
                  {sellerDoc.rating?.toFixed(1)}
                </span>

                <StarIcon className="mt-[2px] h-[1.97rem] w-[2rem] text-brand-secondary" />
              </div>

              <span className="text-[1.3rem] text-[#444444]">Rating</span>
            </div>

            <div className="flex flex-col items-center gap-y-2">
              <div className="flex items-start gap-x-2">
                <span className="text-[2.1rem] font-semibold text-black">
                  {sellerDoc?.reviews ?? 0}
                </span>
              </div>

              <span className="text-[1.3rem] text-[#444444]">Reviews</span>
            </div>

            <div className="flex flex-col items-center gap-y-2">
              <div className="flex items-start gap-x-2">
                <span className="text-[2.1rem] font-semibold text-black">
                  {sellerDoc?.num_sold ?? 0}
                </span>
              </div>

              <span className="text-[1.3rem] text-[#444444]">Items Sold</span>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-y-4">
            <button
              type="button"
              className="flex w-full items-center justify-center rounded-[1rem] border border-zinc-500 bg-inherit p-4 text-[1.8rem] font-semibold text-black"
              onClick={() => {
                openChatModalWithRecipientAndProductId({
                  recipientUid: sellerDoc?.uid ?? '',
                  productId: product_id,
                });
              }}
            >
              Message Seller
            </button>

            <Link
              href={`/profile/${sellerDoc?.username_slug ?? ''}`}
              type="button"
              className="flex w-full items-center justify-center rounded-[1rem] border border-zinc-500 bg-inherit p-4 text-[1.8rem] font-semibold text-black"
            >
              View Store
            </Link>
          </div>
        </div>

        <div className="flex w-full flex-col gap-y-[2.4rem] pl-[2rem]">
          <div className="mt-12 flex flex-col gap-x-20 gap-y-8 sm:mt-0 sm:flex-row sm:justify-between">
            {!!sellerDoc.badge_counts?.quick_replies && (
              <AccoladeBadge
                title="Quick Responder"
                description="Responds within 3 hours."
                icon={<QuickResponderIcon className="h-[4rem] w-[4rem]" />}
              />
            )}

            {!!sellerDoc.badge_counts?.fast_shipper && (
              <AccoladeBadge
                title="Fast Shipper"
                description="Ships within 3 business days."
                icon={<FastShipperIcon className="h-[4rem] w-[4rem]" />}
              />
            )}

            {!!sellerDoc.badge_counts?.as_described && (
              <AccoladeBadge
                title="Accurate Describer"
                description="Items match their descriptions."
                icon={<AccurateDescriberIcon className="h-[4rem] w-[4rem]" />}
              />
            )}

            {!!reviews.length && (
              <Link
                href={`/profile/${sellerDoc?.username_slug ?? ''}`}
                type="button"
                className={`hidden w-fit min-w-[20rem] flex-shrink-0 items-center justify-center rounded-[1rem] border border-black bg-inherit px-8 py-4 text-[1.8rem] font-semibold text-black sm:flex ${
                  !sellerDoc.badge_counts?.quick_replies &&
                  !sellerDoc.badge_counts?.fast_shipper &&
                  !sellerDoc.badge_counts?.as_described
                    ? 'ml-auto'
                    : ''
                }`}
              >
                Show all reviews
              </Link>
            )}
          </div>

          {!!reviews.length && (
            <div className="flex w-[40rem] items-center justify-between pr-8 sm:hidden">
              <h1 className="text-[2.4rem] font-semibold text-black">
                Reviews
              </h1>

              <Link
                href={`/profile/${sellerDoc?.username_slug ?? ''}`}
                className="text-[1.8rem] font-semibold text-brand-secondary"
              >
                See all
              </Link>
            </div>
          )}

          <div className="no-scrollbar mx-auto -mt-4 hidden w-[100rem] sm:block">
            <CarouselComponent
              items={reviews.map((review) => (
                <ReviewCard
                  key={review.id}
                  review={review}
                  className="!w-[30rem]"
                />
              ))}
              incrementBy={1}
              basis="basis-1/3.5"
            />
          </div>

          <div className="flex overflow-x-auto overflow-y-hidden sm:hidden">
            <div className="my-8 flex items-center gap-x-8 pr-32">
              {reviews.map((review, i) => (
                <ReviewCard
                  key={i}
                  review={review}
                  widthClassName="!w-[30rem] flex-shrink-0"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const StarIcon = ({ className }: { className?: string }) => (
  <svg
    width="18"
    height="16"
    viewBox="0 0 18 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M8.83333 12.8083L13.9833 15.9167L12.6167 10.0583L17.1667 6.11667L11.175 5.60834L8.83333 0.0833359L6.49167 5.60834L0.5 6.11667L5.05 10.0583L3.68333 15.9167L8.83333 12.8083Z"
      fill="#C41719"
    />
  </svg>
);

const AccoladeBadge = ({
  title,
  description,
  icon,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
}) => (
  <div className="flex items-center gap-x-4">
    {icon}

    <div className="flex flex-col gap-y-2">
      <span className="text-[1.6rem] font-semibold text-black">{title}</span>

      <p className="text-[1.3rem] text-[#444444]">{description}</p>
    </div>
  </div>
);
